export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public issues?: { path: string; message: string }[], public extra: Record<string, any> = {}) {
    super(message);
  }
}

export type Realm = "pf" | "pj" | "admin";

export async function api<T = unknown>(method: "GET" | "POST" | "PUT" | "PATCH", path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-mm-csrf": "1", ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* sem corpo */
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? "error", data?.message ?? `Erro ${res.status}`, data?.issues, data ?? {});
  }
  return data as T;
}

const dm = (iso: string) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
/** Mensagem do servidor (pt-BR). Bloqueios de período concluído (DEC-072/073/099) citam os extratos afetados, só como texto. */
export const errMsg = (e: unknown) => {
  if (!(e instanceof ApiError)) return "Não foi possível concluir a operação.";
  const base = e.issues?.length ? e.issues.map((i) => i.message).join(" ") : e.message;
  const aff = e.extra?.affectedStatements as { from: string; to: string }[] | undefined;
  if (aff?.length) return `${base} Extrato(s) afetado(s): ${aff.map((a) => `${dm(a.from)} a ${dm(a.to)}`).join("; ")}.`;
  if (e.code === "version_conflict") return "Os dados foram alterados por outra operação. Eles foram recarregados; confira e tente novamente.";
  return base;
};

export interface Me {
  user: { id: string; email: string; displayName: string; role: string; realm: Realm; lastLoginAt: string | null };
  tenant: { id: string; kind: "pf" | "pj"; name: string; plan: string; status: string; currency: string } | null;
  security: { totpEnabled: boolean; totpMandatory: boolean };
  preferences: { theme: "claro" | "escuro" | "automatico" };
  session: { expiresAt: string };
}

export const PLAN_LABEL: Record<string, string> = { teste: "Teste", pf: "Pessoa Física", pj: "Pessoa Jurídica", personalizado: "Personalizado" };
export const ROLE_LABEL: Record<string, string> = {
  usuario_pf: "Usuário PF", usuario_pj: "Usuário PJ", administrador_geral: "Administrador Geral", operador_suporte: "Operador de Suporte",
};
export const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

/** Chave de idempotência por tentativa de formulário: reenviar a mesma tentativa nunca duplica o registro. */
export const newIdemKey = () => ({ "idempotency-key": `web-${crypto.randomUUID()}` });

export interface Entity {
  id: string; kind: "pessoa" | "empresa"; name: string; document: string | null; status: "ativa" | "inativa";
  version: number; permission: "total" | "leitura"; isOwner: boolean; createdAt: string;
}
export interface Money { cents: number; decimal: string }
export interface Account {
  id: string; entityId: string; name: string; type: string; typeLabel: string; currency: "BRL" | "USD";
  institution: string | null; notes: string | null; overdraftLimit: Money; initialBalance: Money; initialBalanceDate: string; balance: Money; projectedBalance: Money; hasMovements: boolean;
  status: "ativa" | "inativa"; inactivatedAt: string | null; inactivationReason: string | null; version: number; createdAt: string; updatedAt: string;
}
export interface AccountType { code: string; label: string; available: boolean; requiredModule: string | null }
export interface HistoryItem { id: number; at: string; action: string; actor: string | null; details: Record<string, any> }
export interface CurrencySummary { currency: "BRL" | "USD"; count: number; total: Money; projected: Money }

export type Nature = "receita" | "despesa";
export type Treatment = "resultado" | "distribuicao_lucros" | "venda_ativo";
export const TREATMENT_LABEL: Record<Treatment, string> = { resultado: "Resultado (operacional)", distribuicao_lucros: "Distribuição de lucros", venda_ativo: "Venda de ativos" };
export interface Category {
  treatment: Treatment;
  id: string; level: 1 | 2 | 3; parentId: string | null; nature: Nature; name: string; position: number;
  status: "ativa" | "inativa"; inactivatedAt: string | null; inactivationReason: string | null; inUse: boolean; version: number;
  /** Classificação reservada ao sistema (cartão de crédito): não aparece nos lançamentos e não é editável. */
  systemKey?: string | null;
}
export interface Counterparty { id: string; name: string; document: string | null; status: "ativa" | "inativa"; version: number; inactivationReason: string | null; /** cliente = pagador (receitas); fornecedor = beneficiário (despesas); ambos = os dois */ role?: "cliente" | "fornecedor" | "ambos" }
export interface CounterpartyPage { items: Counterparty[]; total: number; limit: number; offset: number }
export interface MemoRule {
  id: string; entityId: string | null; matchType: "igual" | "inicia" | "contem"; pattern: string;
  categoryId: string | null; categoryName: string | null; counterpartyId: string | null; counterpartyName: string | null;
  priority: number; usesCount: number; status: "ativa" | "inativa"; version: number;
}

export type TxStatus = "planejado" | "confirmado" | "conciliado" | "cancelado";
export const STATUS_LABEL: Record<TxStatus, string> = { planejado: "Planejado", confirmado: "Confirmado", conciliado: "Conciliado", cancelado: "Cancelado" };
export interface Impact { accountId: string; accountName: string; currency: "BRL" | "USD"; before: { decimal: string }; after: { decimal: string } }
export interface Transaction {
  id: string; entityId: string; accountId: string; accountName: string; currency: "BRL" | "USD"; kind: Nature;
  description: string; amount: Money; plannedDate: string; settledDate: string | null; overdue: boolean;
  categoryId: string; categoryName: string; categoryPath: string; treatment: Treatment;
  counterpartyId: string | null; counterpartyName: string | null; notes: string | null;
  status: TxStatus; cancelledAt: string | null; cancelReason: string | null; version: number; attachmentCount: number;
  /** Série (parcelada/fixa): vem calculada do servidor. Lançamento único tem tudo nulo. */
  seriesId: string | null; seriesKind: "parcelada" | "fixa" | null; installmentNo: number | null; installmentTotal: number | null;
  /** Cartão: espelho da fatura (cardInvoiceDue, planejado) ou pagamento de fatura (cardPaymentId, com o rateio por categoria em `splits`). */
  tradingNoteId?: string | null; cardId?: string | null; cardInvoiceDue?: string | null; cardPaymentId?: string | null; splits?: { categoryPath: string; cents: number; decimal: string }[] | null;
}
export type TransactionResult = Transaction & { impacts: Impact[]; series?: { id: string; kind: "parcelada" | "fixa"; created: number }; affected?: number };
export type Scope = "this" | "following";
export interface TxPage { items: Transaction[]; total: number; limit: number; offset: number }
/** Situação de conciliação e retroatividade vêm calculadas do servidor (motor e domínio); a tela só exibe. */
export interface ReconState { reconciled?: boolean; reconciliationId?: string | null; retroactive?: boolean }
export interface StatementLine extends Transaction, ReconState { entryType: "lancamento"; balanceAfter: Money }

export type TransferStatus = "planejado" | "confirmado" | "cancelado";
export const TRANSFER_STATUS_LABEL: Record<TransferStatus, string> = { planejado: "Planejada", confirmado: "Realizada", cancelado: "Cancelada" };
export interface Transfer {
  id: string; entityId: string;
  fromAccountId: string; fromAccountName: string; fromCurrency: "BRL" | "USD"; fromAmount: Money;
  toAccountId: string; toAccountName: string; toCurrency: "BRL" | "USD"; toAmount: Money;
  crossCurrency: boolean; rate: string | null; rateDisplay: string | null;
  description: string | null; plannedDate: string; settledDate: string | null; overdue: boolean; notes: string | null;
  status: TransferStatus; cancelledAt: string | null; cancelReason: string | null; version: number; createdAt: string; updatedAt: string; attachmentCount: number;
  seriesId: string | null; seriesKind: "parcelada" | "fixa" | null; installmentNo: number | null; installmentTotal: number | null;
}
export type TransferResult = Transfer & { impacts: Impact[]; series?: { id: string; kind: "parcelada" | "fixa"; created: number }; affected?: number };
export interface TransferPage { items: Transfer[]; total: number; limit: number; offset: number }
export interface TransferQuote {
  fromCurrency: "BRL" | "USD"; toCurrency: "BRL" | "USD"; fromAmount: Money; toAmount: Money; crossCurrency: boolean;
  rate: string | null; rateDisplay: string | null; rateUnit: string | null;
  atypicalRate: boolean; referenceRange: { min: string; max: string } | null;
}
export interface TransferStatementLine extends Transfer {
  entryType: "transferencia"; leg: "saida" | "entrada"; legAmount: Money; counterAccountId: string; counterAccountName: string; balanceAfter: Money;
}
export interface TransferStatementLine extends ReconState {}
export type StatementEntry = StatementLine | TransferStatementLine;
export interface Statement { currency: "BRL" | "USD"; opening: Money; total: number; lines: StatementEntry[] }

// ---------------------------------------------------------------- DEC-112: visão financeira integrada (GET /movements). Tudo calculado no servidor.
export type MovSituation = "realizado" | "agendado" | "vencido" | "cancelado";
export type ReconUi = "conciliado" | "parcial" | "nao_conciliado" | "nao_aplicavel";
export const MOV_SITUATION_LABEL: Record<MovSituation, string> = { realizado: "Realizado", agendado: "Agendado", vencido: "Vencido", cancelado: "Cancelado" };
export interface MovCommon {
  situation: MovSituation; block: 0 | 1 | 2; referenceDate: string; signed: Money | null;
  balanceEffective: Money | null; balanceProjected: Money | null; retroactive: boolean;
  reconciliation: { state: ReconUi; reconciliationId: string | null; legs: { saida: string | null; entrada: string | null } | null };
}
export type MovementLine =
  | (Transaction & MovCommon & { entryType: "lancamento" })
  | (Transfer & MovCommon & { entryType: "transferencia"; leg?: "saida" | "entrada"; legAmount?: Money; counterAccountId?: string; counterAccountName?: string });
export interface SummaryColumn { opening: Money; receitas: Money; transfersIn: Money; despesas: Money; transfersOut: Money; resultado: Money; closing: Money }
export interface MovementsSummary { currency: "BRL" | "USD"; effective: SummaryColumn; projected: SummaryColumn }
export interface MovementsView {
  mode: "conta" | "consolidado"; entityId: string; asOf: string; projection: { cutoff: string; cutoffIsEndOfMonth: boolean };
  balances: {
    accounts: { accountId: string; name: string; currency: "BRL" | "USD"; status: "ativa" | "inativa"; current: Money; projected: Money }[];
    totals: { currency: "BRL" | "USD"; accounts: number; current: Money; projected: Money }[];
  };
  summary?: MovementsSummary[]; total: number; limit: number; offset: number; lines: MovementLine[];
}
export interface Suggestion {
  suggestion: { ruleId: string; matchType: string; category: { id: string; name: string; path: string } | null; counterparty: { id: string; name: string } | null } | null;
  patternProposal: string;
}
export interface CatalogPreview { kind: "pf" | "pj"; groups: number; categories: number; canApply: boolean; reason: "owner_only" | "not_empty" | null; items: { name: string; nature: Nature; categories: { name: string; treatment: Treatment }[] }[] }

// ---------------------------------------------------------------- F2-E5 / M5: extratos bancários e conciliação (contratos do M4)
export type StatementStatus = "aberto" | "conciliado";
export interface BankStatement {
  id: string; entityId: string; accountId: string; accountName: string; currency: "BRL" | "USD"; from: string; to: string;
  openingBalance: Money; closingBalance: Money; status: StatementStatus; concludedAt: string | null; concludedBy: string | null;
  lastReopenedAt: string | null; lastReopenReason: string | null; reopenGroupId: string | null; version: number; createdAt: string; updatedAt: string;
}
export interface PeriodSummary { opening: Money; inflow: Money; outflow: Money; closing: Money; transfersIn: Money; transfersOut: Money; movements: number }
export interface StatementSummary {
  period: PeriodSummary; ledgerOpening: Money; ledgerClosing: Money; bankOpening: Money; bankClosing: Money; difference: Money; openingDifference: Money;
  lines: { pending: number; reconciled: number; ignored: number; total: number };
}
export type BankStatementDetail = BankStatement & { summary: StatementSummary };
export interface BankStatementPage { items: BankStatement[]; total: number; limit: number; offset: number }
export type LineStatus = "pendente" | "conciliada" | "ignorada";
export const LINE_STATUS_LABEL: Record<LineStatus, string> = { pendente: "Pendente", conciliada: "Conciliada", ignorada: "Ignorada" };
export interface BankLine {
  id: string; statementId: string; accountId: string; entityId: string; date: string; description: string; document: string | null;
  amount: Money; status: LineStatus; ignoredReason: string | null; duplicateConfirmed: boolean; reconciliationId: string | null; version: number; createdAt: string;
}
export interface BankLinePage { items: BankLine[]; total: number; limit: number; offset: number }
export interface Reconciliation {
  id: string; entityId: string; accountId: string; origin: "MANUAL" | "ASSISTIDA"; lineId: string | null; sourceType: "transaction" | "transfer"; sourceId: string;
  leg: "saida" | "entrada" | null; amount: Money; status: "ativa" | "desfeita"; outsideWindow: boolean; daysDiff: number | null; matchedAt: string; undoReason: string | null;
}

// ---------------------------------------------------------------- F2-E5 / M5: G1 (candidatos), G2 (colagem/CSV), G3 (criar a partir da linha)
export interface Candidate {
  sourceType: "transaction" | "transfer"; sourceId: string; leg: "saida" | "entrada" | null; kind: string; description: string; counterAccountName: string | null;
  date: string; amount: Money; daysDiff: number; outsideWindow: boolean; requiresConfirmation: boolean;
}
export interface CandidatePage {
  lineId: string; statementId: string; windowDays: number; includeOutside: boolean; eligible: boolean; reason: "line_not_pending" | "statement_not_open" | null;
  items: Candidate[]; total: number; limit: number; offset: number;
}
export interface PasteMapping { date?: number | null; description?: number | null; document?: number | null; amount?: number | null; debit?: number | null; credit?: number | null; indicator?: number | null }
export interface PasteRow {
  row: number; sourceLine: number; cells: string[]; status: "ok" | "erro" | "duplicada" | "excluida"; date: string | null; description: string | null; document: string | null; amount: Money | null;
  errors: { field: string; code: string; message: string }[]; duplicates: ({ level: "lote"; ofRow: number } | { level: "gravada"; lineIds: string[] })[];
}
export interface PastePreview {
  statement: { id: string; accountId: string; currency: "BRL" | "USD"; from: string; to: string; version: number };
  limits: { maxLines: number; maxBytes: number }; bytes: number; separator: ";" | "," | "tab"; hasHeader: boolean; headers: string[] | null; columnCount: number;
  mapping: PasteMapping; mappingMissing: string[]; sample: string[][]; numberFormat: "pt-BR" | "internacional"; encoding: string | null; source: "colagem" | "arquivo";
  rows: PasteRow[];
  totals: { lines: number; ok: number; errors: number; duplicates: number; excluded: number; in: Money; out: Money; net: Money };
  comparison: { expectedNet: Money; existingNet: Money; pasteNet: Money; remaining: Money };
  repeatedPaste: { suspected: boolean; matchingRows: number; validRows: number; sameContentLoaded: boolean };
  contentHash: string; blockers: ("mapping_required" | "rows_with_errors" | "no_lines")[]; canSave: boolean;
}
export interface PasteBatchResult { batchId: string; statementId: string; lines: number; duplicatesConfirmed: number; contentHash: string; totalIn: Money; totalOut: Money; net: Money }
export interface FromLineTransaction { transaction: TransactionResult; line: BankLine; reconciliation: Reconciliation }
export interface FromLineTransfer { transfer: TransferResult; leg: "saida" | "entrada"; line: BankLine; reconciliation: Reconciliation }

/** Chave de idempotência da tentativa: reaproveitada em reenvio de rede, renovada quando o servidor respondeu (o corpo pode ter mudado). */
export function makeIdem() {
  let k = newIdemKey();
  return { get: () => k, renew: () => { k = newIdemKey(); } };
}

// ------------------------------------------------------------------ cartão de crédito
export type InvoiceStatus = "aberta" | "fechada" | "paga";
export interface InvoiceSummary {
  dueDate: string; closingDate: string; startDate: string; status: InvoiceStatus; closed: boolean; settled: boolean;
  carryIn: Money; charges: Money; total: Money; paid: Money; remaining: Money; carriedOut: Money; singleCharges: Money; installmentCharges: Money; fixedCharges: Money;
}
export interface Card {
  id: string; entityId: string; name: string; institution: string | null; brand: string | null; notes: string | null; creditLimit: Money; closingDay: number; dueDay: number;
  payAccountId: string; payAccountName: string; used: Money; available: Money; currentInvoice: InvoiceSummary;
  status: "ativa" | "inativa"; inactivatedAt: string | null; inactivationReason: string | null; version: number;
}
export interface InvoiceItem {
  type: "compra" | "pagamento"; id: string; date: string; description: string; amount: Money; categoryPath: string | null; counterpartyName: string | null;
  status: string; seriesKind: "parcelada" | "fixa" | null; installmentNo: number | null; installmentTotal: number | null; paidCents?: Money; notes?: string | null; version?: number; confirmed?: boolean; reconciled?: boolean;
  finalSettlement?: boolean; payAccountName?: string;
}
export interface InvoiceView { card: Card; invoice: InvoiceSummary; items: InvoiceItem[]; prevDue: string; nextDue: string; firstDue: string | null; lastDue: string | null }
export interface Charge {
  id: string; cardId: string; description: string; amount: Money; paid: Money; purchaseDate: string; dueDate: string; categoryId: string; categoryPath: string;
  counterpartyId: string | null; counterpartyName: string | null; notes: string | null; status: "ativa" | "cancelada"; version: number; confirmed: boolean; reconciled: boolean;
  seriesId: string | null; seriesKind: "parcelada" | "fixa" | null; installmentNo: number | null; installmentTotal: number | null;
}
export type ChargeResult = Charge & { series?: { id: string; kind: "parcelada" | "fixa"; created: number }; affected?: number };

/** Detalhamento do período de uma conta (Extratos) e uso do limite da conta (cheque especial). */
export interface AccountPeriodDetail {
  accountId: string; currency: "BRL" | "USD"; type: string; from: string; to: string;
  expenses: { singles: Money; fixed: Money; installments: Money };
  limit: { total: Money; used: Money; available: Money };
}

/** Investimentos / Futuros: notas de corretagem importadas do PDF. */
export interface NoteTrade { line?: number; side: "C" | "V"; instrument: string; assetCode: string; maturity: string; quantity: number; price: string; tradeType: string; amount: Money; result?: Money; cost?: Money; resultAssetCents?: Money; costShare?: Money }
export interface TradingNote {
  id: string; entityId: string; accountId: string; accountName: string; brokerName: string; brokerCnpj: string; noteNumber: string; tradeDate: string;
  gross: Money; costs: Money; irrf: Money; net: Money; tradesCount: number; assets: string[]; pdfName: string; status: "ativa" | "cancelada"; cancelReason: string | null; version: number; reconState: "pendente" | "parcial" | "conciliada";
  trades?: NoteTrade[]; transactions?: { id: string; kind: string; description: string; amount: Money; status: string }[];
}
export interface NotePreview {
  broker: { name: string; cnpj: string }; noteNumber: string; sheet: number; tradeDate: string; trades: NoteTrade[]; gross: Money; costs: Money; irrf: Money; net: Money;
  costBreakdown: { operationalFee: Money; registryFee: Money; exchangeFees: Money; other: Money; taxes: Money }; warnings: string[]; sha256: string; duplicate: { id: string; reason: string } | null;
}
export interface TradingSummary {
  from: string; to: string; notes: number; days: number; winDays: number; contracts: number; gross: Money; costs: Money; irrf: Money; net: Money;
  byAsset: { assetCode: string; name: string; trades: number; contracts: number; result: Money; costs: Money; net: Money }[];
  byDay: { date: string; gross: Money; costs: Money; irrf: Money; net: Money }[];
}
/** Envia o PDF da nota (corpo binário; senha opcional no cabeçalho). */
export async function postNote<T>(path: string, file: File, password?: string): Promise<T> {
  const res = await fetch(path, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/octet-stream", "x-mm-csrf": "1", ...(password ? { "x-pdf-password": password } : {}) }, body: file });
  let data: any = null;
  try { data = await res.json(); } catch { /* sem corpo */ }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? "error", data?.message ?? (res.status === 413 ? "Arquivo grande demais (máximo de 5 MB)." : `Erro ${res.status}`), data?.issues, data ?? {});
  return data as T;
}

/** Fechamento de posição por conta e mês. */
export interface ClosingStatus {
  accountId: string; month: string; closed: boolean; closedAt: string | null; closingBalance: Money | null; closedThrough: string | null; canClose: boolean; canReopen: boolean;
  blockers: { monthNotEnded: boolean; previousOpen: string | null; beforeAccountStart: boolean; planned: number; unreconciled: number };
}
